// frontend/src/pages/FuelLogPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';
import Alert from 'react-bootstrap/Alert';
import { FuelLogForm } from '../components/forms/FuelLogForm'; // Importar el formulario

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Interfaz para el Log de Combustible (ajusta según tu BD/API)
interface FuelLog {
  id: number;
  asset_id: number;
  log_date: string;
  quantity_liters: number;
  cost_per_liter: number;
  total_cost: number; // Asumiendo que el backend lo calcula y devuelve
  odometer_reading: number | null;
  attachment_path: string | null;
  // Podrías añadir campos de Asset aquí si el backend los incluye
  asset_name?: string;
  asset_identifier?: string;
}

export function FuelLogPage() {
  const [logs, setLogs] = useState<FuelLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Función para cargar los registros
  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Pedimos los últimos 20 registros, ordenados descendente
      // Asegúrate que el endpoint GET /api/data/fuel-logs exista en backend
      const response = await fetch(`${API_BASE_URL}/api/data/fuel-logs?limit=20&sort=desc`);
      if (!response.ok) {
        // Intenta obtener mensaje de error del backend
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Error al obtener los registros de combustible.');
      }
      const data: FuelLog[] = await response.json();
      // Formatear fecha
      const formattedData = data.map(log => ({
          ...log,
          log_date: new Date(log.log_date).toLocaleString() // Formato local
      }));
      setLogs(formattedData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
      console.error("Error fetching fuel logs:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Cargar registros al montar
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return (
    <Container fluid className="mt-3">
      <Row>
        {/* Columna para la tabla de registros */}
        <Col md={8} className="mb-3">
          <h3>Registros de Combustible Recientes</h3>
          {isLoading && <p>Cargando registros...</p>}
          {error && <Alert variant="danger">{error}</Alert>}
          {!isLoading && !error && (
            <Table striped bordered hover responsive size="sm">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Fecha</th>
                  <th>Activo ID</th> {/* Mejorar con Nombre/ID */}
                  <th>Litros</th>
                  <th>$/Litro</th>
                  <th>Costo Total</th>
                  <th>Odómetro</th>
                  <th>Adjunto</th>
                </tr>
              </thead>
              <tbody>
                {logs.length > 0 ? (
                  logs.map(log => (
                    <tr key={log.id}>
                      <td>{log.id}</td>
                      <td>{log.log_date}</td>
                      <td>{log.asset_id} {log.asset_identifier ? `(${log.asset_identifier})` : ''}</td>
                      <td>{log.quantity_liters.toFixed(2)}</td>
                      <td>${(log.cost_per_liter || 0).toFixed(2)}</td>
                      <td>${(log.total_cost || 0).toFixed(2)}</td>
                      <td>{log.odometer_reading || '-'}</td>
                      <td>
                        {log.attachment_path ? (
                          // Enlaza al endpoint que sirve archivos (ajusta la URL base si es necesario)
                          <a
                            href={`${API_BASE_URL}/uploads/${log.attachment_path}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Ver
                          </a>
                        ) : '-'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="text-center">No hay registros de combustible.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Col>

        {/* Columna para el formulario */}
        <Col md={4}>
          {/* Refrescar la lista cuando se crea un nuevo log */}
          <FuelLogForm onLogCreated={fetchLogs} />
        </Col>
      </Row>
    </Container>
  );
}